import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Flex,
  Box,
  useToast,
  useColorModeValue,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Icon,
} from '@chakra-ui/react'
import { FaEdit, FaCheck, FaUser, FaEnvelope } from 'react-icons/fa'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { userService } from '../services/api'
import { formatDate } from '../utils/helpers'

const UserInfoCard = () => {
  const toast = useToast()
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const queryClient = useQueryClient()

  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const labelColor = useColorModeValue('gray.600', 'gray.400')
  const inputBg = useColorModeValue('white', 'gray.800')

  // Fetch user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ['userData'],
    queryFn: async () => {
      const response = await userService.getUser()
      return response.data
    },
  })

  // Update user info mutation
  const updateUserMutation = useMutation({
    mutationFn: async (userData: { name: string; contact: string }) => {
      return await userService.updateUser(userData.name, userData.contact)
    },
    onSuccess: () => {
      toast({
        title: 'Profile Updated',
        description: 'Your information has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      queryClient.invalidateQueries({ queryKey: ['userData'] })
      onClose()
    },
    onError: (error: any) => {
      toast({
        title: 'Update Failed',
        description:
          error.message || 'There was an error updating your profile.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    },
  })

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateUserMutation.mutate({ name, contact })
  }

  // Initialize form with current user data when opening modal
  const handleOpenModal = () => {
    // @ts-ignore
    setName(userData?.name || '')
    // @ts-ignore
    setContact(userData?.contact || '')
    onOpen()
  }
  // @ts-ignore
  const hasUserInfo = userData?.user?.name || userData?.user?.contact

  return (
    <Card
      bg={cardBg}
      boxShadow="md"
      borderColor={borderColor}
      borderWidth="1px"
    >
      <CardHeader>
        <Flex justify="space-between" align="center">
          <Heading size="md">Personal Information</Heading>
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            leftIcon={<Icon as={FaEdit} />}
            onClick={handleOpenModal}
          >
            {hasUserInfo ? 'Edit' : 'Add Info'}
          </Button>
        </Flex>
      </CardHeader>
      <CardBody>
        <Stack spacing={4}>
          {isLoading ? (
            <Text>Loading user information...</Text>
          ) : hasUserInfo ? (
            <>
              <Flex align="center" gap={3}>
                <Icon as={FaUser} color="blue.500" />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={labelColor}>
                    Full Name
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {/* @ts-ignore */}

                    {userData?.user?.name || 'Not provided'}
                  </Text>
                </Box>
              </Flex>

              <Divider />

              <Flex align="center" gap={3}>
                <Icon as={FaEnvelope} color="blue.500" />
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={labelColor}>
                    Contact Information
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {/* @ts-ignore */}
                    {userData?.user?.contact || 'Not provided'}
                  </Text>
                </Box>
              </Flex>

              <Divider />

              {/* @ts-ignore */}

              {userData?.last_login && (
                <Text fontSize="xs" color={labelColor} textAlign="right">
                  {/* @ts-ignore */}
                  Last login: {formatDate(userData.last_login)}
                </Text>
              )}
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Text mb={2} color={labelColor}>
                No personal information provided yet.
              </Text>
              <Text fontSize="sm" color={labelColor}>
                Click the "Add Info" button to set up your profile.
              </Text>
            </Box>
          )}
        </Stack>
      </CardBody>

      {/* Edit Information Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Profile Information</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Stack spacing={4}>
                <FormControl id="name">
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    bg={inputBg}
                  />
                </FormControl>
                <FormControl id="contact">
                  <FormLabel>Contact Information</FormLabel>
                  <Input
                    placeholder="Email or phone number"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    bg={inputBg}
                  />
                </FormControl>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                isLoading={updateUserMutation.isPending}
                leftIcon={<Icon as={FaCheck} />}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Card>
  )
}

export default UserInfoCard
